import { useEffect, useState } from 'react';

import { fetchPackages } from '../api';
import { navigateTo } from '../router';
import type { LcRouteParams } from '../types';

interface VersionSwitcherProps {
  params: LcRouteParams;
  buildRoute: (params: LcRouteParams) => string;
}

interface VersionOption {
  version: string;
  displayVersion: string;
}

function sortVersionsDesc(versions: VersionOption[]): VersionOption[] {
  return [...versions].sort((a, b) => {
    const normalizedA = (a.displayVersion || a.version).replace(/^v(?=\d)/i, '');
    const normalizedB = (b.displayVersion || b.version).replace(/^v(?=\d)/i, '');
    const aParts = normalizedA.split('.').map(Number);
    const bParts = normalizedB.split('.').map(Number);
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const diff = (bParts[i] ?? 0) - (aParts[i] ?? 0);
      if (diff !== 0) return diff;
    }
    return 0;
  });
}

export function VersionSwitcher({ params, buildRoute }: VersionSwitcherProps) {
  const [versions, setVersions] = useState<VersionOption[]>([]);

  useEffect(() => {
    let isMounted = true;

    fetchPackages()
      .then((packages) => {
        if (!isMounted) return;
        const matching = packages
          .filter((p) => p.registry === params.registry && p.name === params.name)
          .map((p) => ({
            version: p.version,
            displayVersion: p.display_version ?? p.version,
          }));
        setVersions(sortVersionsDesc(matching));
      })
      .catch(() => {
        // Silently ignore — version switcher is non-critical
      });

    return () => {
      isMounted = false;
    };
  }, [params.registry, params.name]);

  if (versions.length <= 1) {
    const onlyVersion = versions[0];
    return (
      <span className="font-mono text-sm text-dim">
        {onlyVersion?.displayVersion ?? params.version}
      </span>
    );
  }

  const latestVersion = versions[0];

  return (
    <select
      value={params.version}
      onChange={(e) => {
        const next = e.target.value;
        if (next !== params.version) {
          navigateTo(buildRoute({ ...params, version: next }));
        }
      }}
      className="border border-rule bg-panel px-2 py-1 font-mono text-sm text-link"
    >
      {versions.map((v) => (
        <option key={v.version} value={v.version}>
          {v.displayVersion}
          {v.version === latestVersion.version ? ' (latest)' : ''}
        </option>
      ))}
    </select>
  );
}
